import React, { useEffect, useState } from "react";
import { Megaphone, Check, X, RefreshCw, Play, Pause } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUSES = [
  { id: "pending_review", label: "Pending" },
  { id: "live", label: "Live" },
  { id: "completed", label: "Completed" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

export default function AdsReviewCard() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/ads?status=${status}`, { headers: authHeaders() });
      setRows(data || []);
    } catch (_) {
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const review = async (orderId, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejection (will be emailed to advertiser):", "");
      if (reason === null) return; // cancelled
    } else {
      if (!window.confirm("Approve this ad? It will start airing immediately across every station.")) return;
    }
    setBusy(orderId);
    try {
      await api.post(`/admin/ads/${orderId}/review`, { action, reason }, { headers: authHeaders() });
      setMsg(action === "approve" ? "Approved · now in rotation worldwide" : "Rejected · advertiser emailed");
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Action failed");
    }
    setBusy("");
  };

  const toggleAudio = (orderId, url) => {
    const id = `ad-audio-${orderId}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("audio");
      el.id = id;
      el.src = url;
      document.body.appendChild(el);
    }
    if (playing === orderId) {
      el.pause();
      setPlaying(null);
    } else {
      // Pause any other previewer first
      document.querySelectorAll("audio[id^='ad-audio-']").forEach((a) => a.pause());
      el.play().catch(() => {});
      setPlaying(orderId);
    }
  };

  return (
    <section className="mt-10" data-testid="ads-review-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="fl-display text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-emerald-500" /> Sponsor ads
        </h2>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className={`fl-pill ${status === s.id ? "is-active" : ""}`}
              data-testid={`ads-status-${s.id}`}
            >
              {s.label}
            </button>
          ))}
          <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="ads-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {msg && <div className="text-xs text-emerald-400 mb-3" data-testid="ads-msg">{msg}</div>}
      {rows.length === 0 ? (
        <div className="fl-card p-8 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">
          // No {status === "all" ? "" : status.replace("_", " ")} ads
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.order_id} className="fl-card p-4" data-testid={`ad-row-${r.order_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">{r.business_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {r.contact_email} · ${r.amount} · {r.package_id === "ad_5min" ? "5-min" : "3-min"} · {r.days}d
                  </div>
                  <div className="text-[11px] fl-mono uppercase tracking-widest text-slate-500 mt-1.5">
                    {r.status.replace("_", " ")}
                    {r.audio_duration ? ` · ${Math.round(r.audio_duration)}s` : ""}
                    {r.play_count ? ` · ${r.play_count} plays` : ""}
                    {r.expires_at ? ` · expires ${r.expires_at.slice(0,10)}` : ""}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.audio_url && (
                    <button
                      onClick={() => toggleAudio(r.order_id, `${process.env.REACT_APP_BACKEND_URL}${r.audio_url}`)}
                      className="fl-btn ghost !py-2 !px-3 text-xs"
                      data-testid={`ad-preview-${r.order_id}`}
                    >
                      {playing === r.order_id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      {playing === r.order_id ? "Stop" : "Preview"}
                    </button>
                  )}
                  {r.status === "pending_review" && (
                    <>
                      <button
                        onClick={() => review(r.order_id, "approve")}
                        disabled={busy === r.order_id}
                        className="fl-btn !py-2 !px-3 text-xs"
                        data-testid={`ad-approve-${r.order_id}`}
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => review(r.order_id, "reject")}
                        disabled={busy === r.order_id}
                        className="fl-btn ghost !py-2 !px-3 text-xs"
                        data-testid={`ad-reject-${r.order_id}`}
                      >
                        <X className="w-4 h-4 text-red-400" /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
              {r.reject_reason && (
                <div className="text-xs text-red-300 mt-2">Reason: {r.reject_reason}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
